import { ENV } from '../config/env';

/**
 * Get the full URL for a file stored in Laravel's public storage disk.
 *
 * Handles both path formats returned by the API:
 *   - New format: "property-images/xxx.webp"         → /storage/property-images/xxx.webp
 *   - Old format: "storage/property-images/xxx.webp" → /storage/property-images/xxx.webp
 *                 (strips the leading "storage/" so it isn't doubled)
 *
 * @param filePath - The relative path from the DB or API response
 * @returns The full URL to access the file
 */
export const getFileUrl = (filePath: string): string => {
  if (!filePath) return '';

  // If the backend has already returned a full URL, return it as-is
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }

  // Strip the trailing /api path segment to get the storage root host
  const baseUrl = ENV.API_BASE_URL.replace(/\/api$/, '');

  // Normalise: strip any leading "storage/" to avoid doubling
  const normalised = filePath.replace(/^storage\//, '');

  return `${baseUrl}/storage/${normalised}`;
};

/**
 * Get the profile picture URL for a user
 * @param user - The user object with user_data property
 * @returns The profile picture URL or default avatar path
 */
/**
 * Preserve provider and private API URLs, including their signatures. Rewriting
 * their host or path breaks archived evidence and expiring provider links.
 */
export const getDojahImageUrl = (url: string | null): string | null => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    return ['https:', 'http:'].includes(parsed.protocol) && !parsed.username && !parsed.password ? url : null;
  } catch {
    return null;
  }
};

export const getProfilePictureUrl = (user: unknown): string => {
  const u = user as { user_data?: { profile_picture?: string } } | null;
  if (u?.user_data?.profile_picture) {
    return getFileUrl(u.user_data.profile_picture);
  }
  return ''; // ProfileAvatar renders the stable default icon.
};
